import { ref } from "vue"
import { createLoadGate } from "./loadGate.js"

/**
 * Attach hls.js to a <video> element. The HLS URL comes from the page (built
 * from podcast.json's episode.video.version), so this composable owns
 * playback wiring only — no discovery / no HTTP at runtime.
 *
 * Safari (incl. iOS) plays HLS natively; everywhere else, hls.js polyfills.
 *
 * Exposes reactive `levels` / `currentLevel` so the UI can render a quality
 * picker. `setLevel(-1)` returns to auto-ABR; `setLevel(n)` pins a rung.
 *
 * onFatalError (SC-1323): called once after a fatal non-403 hls.js error destroyed the instance.
 */
export function useVideoPlayback({ onFatalError = null, loadHls = () => import("hls.js") } = {}) {
    const levels = ref([])
    const currentLevel = ref(-1)
    const isNative = ref(false)
    // Flips to true when the stream endpoints start returning 403 mid-playback
    // (traffic quota was reached after the page loaded). Lets the page swap in
    // the "video unavailable" screen instead of leaving hls.js to retry a dead
    // stream. Only the hls.js path can detect this — native HLS (iOS Safari)
    // surfaces a generic media error with no status code, so there a reload is
    // needed to pick up the server-rendered blocked state.
    const blocked = ref(false)

    let hlsModule = null
    let hlsInstance = null
    let loadGate = null

    async function attach(videoEl, hlsUrl) {
        if (!videoEl || !hlsUrl) return

        levels.value = []
        currentLevel.value = -1
        isNative.value = false
        blocked.value = false

        // iOS Safari MUST use the browser's native HLS engine — hls.js can run
        // via MSE on iOS 17+, but playback is unreliable and there's no point
        // since iOS does its own ABR well. Desktop Safari, on the other hand,
        // gets hls.js so the user has a manual quality picker.
        const ua = typeof navigator !== "undefined" ? navigator.userAgent : ""
        const isIOS = /iPad|iPhone|iPod/.test(ua)
        const canPlayNative = !!videoEl.canPlayType("application/vnd.apple.mpegurl")

        if (isIOS && canPlayNative) {
            // Re-attach can happen when the parent swaps the HLS URL (episode
            // change) on a kept-alive component. A previously-created hls.js
            // instance is still wired to videoEl via MediaSource — destroy it
            // before pointing .src at a native URL or the old MSE buffer
            // leaks and audio/video can desync.
            // With preload="none" Safari fetches nothing until play().
            destroy()
            isNative.value = true
            videoEl.src = hlsUrl
            return
        }

        if (!hlsModule) {
            hlsModule = await loadHls()
        }
        const Hls = hlsModule.default

        if (!Hls.isSupported()) {
            // Desktop browser without MSE — fall back to native HLS if the
            // browser exposes one (only Safari does today).
            if (canPlayNative) {
                destroy()
                isNative.value = true
                videoEl.src = hlsUrl
            } else {
                console.warn("[VideoStream] no HLS engine available in this browser")
            }
            return
        }

        destroy()
        hlsInstance = new Hls({
            enableWorker: true,
            // Auto-pick a starting rung. The default of -1 starts at the first
            // rung in the manifest, which for our ascending ladder is 360p.
            // Letting hls.js choose based on the bandwidth probe gives a much
            // better first impression on broadband.
            startLevel: -1,
            // Don't waste bandwidth on a rung above the player's pixel size.
            capLevelToPlayerSize: true,
            // Start with an optimistic bandwidth estimate (5 Mbps) so the
            // initial level pick is closer to the real connection capacity;
            // the EWMA settles into the actual measurement within seconds.
            abrEwmaDefaultEstimate: 5_000_000,
            // IAB: only the master manifest before the first play; the load
            // gate starts variant playlists and segments on that play.
            autoStartLoad: false,
        })
        loadGate = createLoadGate(videoEl, hlsInstance)

        const syncLevels = () => {
            if (!hlsInstance) return
            const list = hlsInstance.levels ?? []
            levels.value = list.map((l, idx) => ({
                index: idx,
                height: l.height,
                bitrate: l.bitrate,
            }))
        }

        hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
            syncLevels()
            // Backup re-sync — some versions of hls.js mutate the levels array
            // after MANIFEST_PARSED (e.g. dropping unsupported codecs), and a
            // race with our listener-registration can leave levels empty.
            setTimeout(syncLevels, 0)
        })
        hlsInstance.on(Hls.Events.LEVELS_UPDATED, syncLevels)

        hlsInstance.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
            currentLevel.value = data.level
        })

        hlsInstance.on(Hls.Events.ERROR, (_, data) => {
            // A 403 on the manifest, variant or a fragment means Pro flagged
            // this podcast as traffic-blocked. Stop retrying and signal the
            // page so it can render the blocked screen.
            if (data?.response?.code === 403) {
                blocked.value = true
                destroy()
                return
            }
            // SC-1323: the portal page reports fatal stream errors through
            // onFatalError. The podcast page passes no callback and keeps warning only.
            if (data?.fatal && onFatalError) {
                destroy()
                onFatalError()
                return
            }
            console.warn("[VideoStream] hls.js error", data)
        })

        hlsInstance.loadSource(hlsUrl)
        hlsInstance.attachMedia(videoEl)

        // Defensive: if MANIFEST_PARSED already fired by the time we got here
        // (rare but observed during HMR), pick up the levels list directly.
        if (hlsInstance.levels?.length) {
            syncLevels()
        }
    }

    function setLevel(n) {
        if (!hlsInstance) return
        hlsInstance.currentLevel = n
        if (n === -1) {
            hlsInstance.nextLevel = -1
        }
    }

    // Audio/video switch: stop fetching segments while the <audio> plays and
    // resume at the given position on the way back. Native HLS (iOS) has no
    // load control; there these are no-ops.
    function stopLoad() {
        loadGate?.stopLoad()
    }

    function startLoad(seconds) {
        loadGate?.startLoad(seconds)
    }

    function destroy() {
        loadGate?.dispose()
        loadGate = null

        if (hlsInstance) {
            hlsInstance.destroy()
            hlsInstance = null
        }
        levels.value = []
        currentLevel.value = -1
    }

    return { attach, destroy, stopLoad, startLoad, levels, currentLevel, setLevel, isNative, blocked }
}
